/**
 * Экран режимов «Developer Pack» и «Email and Web Pack».
 *
 * Пользователю показывается, какое сочетание он осваивает сейчас, какую
 * клавишу меняет Shift и — прежде всего — что раскладку нужно переключить на
 * латинскую: без этого ни `@`, ни `{` он просто не наберёт (ТЗ §6, режимы 11
 * и 12).
 *
 * Три эти вещи стоят над текстом; список всех открытых сочетаний и объяснение
 * режима — под ним (ТЗ FR-EN-84).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import { Card } from '@type2x/ui';
import type { PacksPlan } from '@type2x/feature-packs';

import type { Runtime } from '../runtime.js';
import { DrillLayout } from './DrillLayout.js';
import { TypingScreen } from './TypingScreen.js';

export interface PacksScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly plan: PacksPlan;
  readonly sessionId: SessionId;
  readonly onFinished: (run: TypingSession) => void;
}

export function PacksScreen({
  runtime,
  t,
  plan,
  sessionId,
  onFinished,
}: PacksScreenProps): React.JSX.Element {
  const title = t(`packs.title.${plan.mode}`);
  if (plan.group === null) return <Card title={title}>{t('packs.done')}</Card>;

  const total = plan.totalGroups;

  return (
    <DrillLayout
      t={t}
      title={title}
      now={
        <>
          {/* Про латинскую раскладку человек должен прочитать до первого
              нажатия, а не найти под раскрытием после десятой ошибки. */}
          <span className="t2x-drill__warning">{t('packs.layout')}</span>{' '}
          <span className="t2x-muted">
            {t('packs.group', { tokens: plan.group.tokens.join(' ') })}
          </span>
          {plan.shiftHints.length > 0 && (
            <ul className="t2x-shift-hints" aria-label={t('packs.shift')}>
              {plan.shiftHints.map((hint) => (
                <li key={hint.character}>
                  {t('packs.shiftHint', { character: hint.character, base: hint.base })}
                </li>
              ))}
            </ul>
          )}
        </>
      }
      progress={{
        value: total === 0 ? 0 : plan.completedGroupIds.length / total,
        label: t('packs.progress'),
      }}
      details={
        <>
          {/* Подпись видимая: крупная строка всех открытых сочетаний читается
              как задание, хотя осваивается лишь названная выше группа
              (ТЗ FR-EN-65). */}
          <p className="t2x-muted">
            {t('packs.unlockedCount', {
              open: plan.unlockedTokens.length,
              total: plan.totalTokens,
              tokens: plan.unlockedTokens.join(' '),
            })}
          </p>

          <p className="t2x-muted">{t(`packs.hint.${plan.mode}`)}</p>
        </>
      }
    >
      <TypingScreen
        key={sessionId}
        runtime={runtime}
        t={t}
        title={t('packs.typing')}
        hint={t(`packs.hint.${plan.mode}`)}
        sessionId={sessionId}
        layoutId={plan.layoutId}
        targetText={plan.exercise.text}
        hintLevel={plan.hintLevel}
        compact
        onFinished={onFinished}
      />
    </DrillLayout>
  );
}
